// SPDX-License-Identifier: AGPL-3.0-or-later

import {NameStyleText} from '@app/features/name_style/components/NameStyleText';
import {colorToHex} from '@app/features/name_style/NameStyle';
import {requestNameStyleFont} from '@app/features/name_style/NameStyleFontLoader';
import {nameStyleFontFamily} from '@app/features/name_style/NameStyleFonts';
import {getPremiumProductName} from '@app/features/premium/utils/PremiumUtils';
import {Button} from '@app/features/ui/button/Button';
import {ColorPickerField} from '@app/features/ui/components/form/ColorPickerField';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {PlutoniumUpsell} from '@app/features/ui/plutonium_upsell/PlutoniumUpsell';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {
	DEFAULT_NAME_COLOR,
	DEFAULT_SECOND_NAME_COLOR,
	isNameStyleReadable,
	type NameColorMode,
	type PlusAppearanceAccess,
	type PlusAppearanceValues,
	PROFILE_THEME_PRESETS,
	withNameColor,
	withNameColorMode,
	withNameFont,
	withThemeColor,
} from '@app/features/user/components/modals/tabs/my_profile_tab/PlusAppearanceForm';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/PlusAppearanceSettings.module.css';
import {profileGradientSurface} from '@app/features/user/components/profile/ProfileGradientSurface';
import {nameReadsOnProfileTheme} from '@app/features/user/utils/ProfileGradient';
import {isNameColorsReadableOnLight} from '@fluxer/constants/src/ColorContrast';
import {NAME_STYLE_FONTS, type NameStyleFont, type NameStyleFontId} from '@fluxer/constants/src/NameStyleConstants';
import type {NameStyle, ThemeColors} from '@fluxer/schema/src/domains/user/PlusAppearanceSchemas';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CheckIcon} from '@phosphor-icons/react';
import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef} from 'react';

const PLUS_APPEARANCE_DESCRIPTOR = msg({
	message: '{premiumProductName} appearance',
	comment: 'Profile settings section title for the name style and profile theme. Preserve {premiumProductName}.',
});
const PLUS_APPEARANCE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Give your name a font and color, and your profile card a two-color theme.',
	comment: 'Profile settings section description for the name style and profile theme. Keep the tone plain.',
});
const PLUS_APPEARANCE_UPSELL_DESCRIPTOR = msg({
	message: 'Name styles and profile themes are part of {premiumProductName}.',
	comment: 'Upsell shown in the profile settings to accounts without Plus. Preserve {premiumProductName}.',
});
const PROFILE_PREVIEW_DESCRIPTOR = msg({
	message: 'Profile preview',
	comment: 'Label above the small live preview of the name style on the profile theme in the profile settings.',
});
const NAME_FONT_DESCRIPTOR = msg({
	message: 'Name font',
	comment: 'Label above the list of fonts for the display name. Keep it concise.',
});
const FONT_ROUNDED_DESCRIPTOR = msg({
	message: 'Rounded',
	comment:
		'Accessible name of a name font option: a soft, rounded typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_PLAYFUL_HANDWRITING_DESCRIPTOR = msg({
	message: 'Playful handwriting',
	comment:
		'Accessible name of a name font option: a casual, playful handwritten typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_RETRO_SCRIPT_DESCRIPTOR = msg({
	message: 'Retro script',
	comment:
		'Accessible name of a name font option: a retro, flowing script typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_HANDWRITING_DESCRIPTOR = msg({
	message: 'Handwriting',
	comment:
		'Accessible name of a name font option: a natural handwritten typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_PIXEL_DESCRIPTOR = msg({
	message: 'Pixel',
	comment:
		'Accessible name of a name font option: a pixel-art typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_ARCADE_PIXEL_DESCRIPTOR = msg({
	message: 'Arcade pixel',
	comment:
		'Accessible name of a name font option: a wide pixel typeface from arcade games. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_TERMINAL_DESCRIPTOR = msg({
	message: 'Terminal',
	comment:
		'Accessible name of a name font option: a typeface from old computer terminals. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_TECHNO_DESCRIPTOR = msg({
	message: 'Techno',
	comment:
		'Accessible name of a name font option: a wide, futuristic techno typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_GOTHIC_DESCRIPTOR = msg({
	message: 'Gothic',
	comment:
		'Accessible name of a name font option: a blackletter, gothic typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_ROCK_DESCRIPTOR = msg({
	message: 'Rock',
	comment:
		'Accessible name of a name font option: a heavy rock-poster typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_BOLD_BLOCK_DESCRIPTOR = msg({
	message: 'Bold block',
	comment:
		'Accessible name of a name font option: a heavy, blocky display typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_RETRO_MODERN_DESCRIPTOR = msg({
	message: 'Retro modern',
	comment:
		'Accessible name of a name font option: a rounded retro-modern display typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_ELEGANT_SCRIPT_DESCRIPTOR = msg({
	message: 'Elegant script',
	comment:
		'Accessible name of a name font option: a flowing, elegant script typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_BOLD_SCRIPT_DESCRIPTOR = msg({
	message: 'Bold script',
	comment:
		'Accessible name of a name font option: a heavy, connected script typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_SPORTS_PIXEL_DESCRIPTOR = msg({
	message: 'Sports pixel',
	comment:
		'Accessible name of a name font option: a pixel typeface like the numbers on a sports jersey. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_PIRATE_DESCRIPTOR = msg({
	message: 'Pirate',
	comment:
		'Accessible name of a name font option: a sharp blackletter typeface with a pirate feel. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_SOFT_GEOMETRIC_DESCRIPTOR = msg({
	message: 'Soft geometric',
	comment:
		'Accessible name of a name font option: a round, geometric typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_WIDE_DESCRIPTOR = msg({
	message: 'Wide',
	comment:
		'Accessible name of a name font option: a very wide, bold typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_CONDENSED_DESCRIPTOR = msg({
	message: 'Condensed',
	comment:
		'Accessible name of a name font option: a tall, narrow all-caps typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_MONOSPACE_DESCRIPTOR = msg({
	message: 'Monospace',
	comment:
		'Accessible name of a name font option: a monospaced typeface where every letter is the same width. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_COMIC_DESCRIPTOR = msg({
	message: 'Comic',
	comment:
		'Accessible name of a name font option: a loud comic-book lettering typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_STENCIL_DESCRIPTOR = msg({
	message: 'Stencil',
	comment:
		'Accessible name of a name font option: a military stencil typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_ROYAL_DESCRIPTOR = msg({
	message: 'Royal',
	comment:
		'Accessible name of a name font option: a decorative serif typeface with classical capitals. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_SPORTY_DESCRIPTOR = msg({
	message: 'Sporty',
	comment:
		'Accessible name of a name font option: a sturdy, sporty display typeface. The option shows the user’s own name in that font. Keep it short.',
});
const FONT_STYLE_DESCRIPTORS: Record<NameStyleFont['key'], MessageDescriptor> = {
	baloo: FONT_ROUNDED_DESCRIPTOR,
	playpen: FONT_PLAYFUL_HANDWRITING_DESCRIPTOR,
	pacifico: FONT_RETRO_SCRIPT_DESCRIPTOR,
	caveat: FONT_HANDWRITING_DESCRIPTOR,
	pixelify: FONT_PIXEL_DESCRIPTOR,
	pressstart: FONT_ARCADE_PIXEL_DESCRIPTOR,
	vt323: FONT_TERMINAL_DESCRIPTOR,
	audiowide: FONT_TECHNO_DESCRIPTOR,
	grenze: FONT_GOTHIC_DESCRIPTOR,
	newrocker: FONT_ROCK_DESCRIPTOR,
	bungee: FONT_BOLD_BLOCK_DESCRIPTOR,
	righteous: FONT_RETRO_MODERN_DESCRIPTOR,
	dancing: FONT_ELEGANT_SCRIPT_DESCRIPTOR,
	lobster: FONT_BOLD_SCRIPT_DESCRIPTOR,
	jersey: FONT_SPORTS_PIXEL_DESCRIPTOR,
	pirata: FONT_PIRATE_DESCRIPTOR,
	comfortaa: FONT_SOFT_GEOMETRIC_DESCRIPTOR,
	unbounded: FONT_WIDE_DESCRIPTOR,
	bebas: FONT_CONDENSED_DESCRIPTOR,
	spacemono: FONT_MONOSPACE_DESCRIPTOR,
	bangers: FONT_COMIC_DESCRIPTOR,
	blackops: FONT_STENCIL_DESCRIPTOR,
	cinzel: FONT_ROYAL_DESCRIPTOR,
	russo: FONT_SPORTY_DESCRIPTOR,
};
const NAME_COLOR_DESCRIPTOR = msg({
	message: 'Name color',
	comment: 'Label for the display name color controls. Keep it concise.',
});
const SOLID_DESCRIPTOR = msg({
	message: 'Solid',
	comment: 'Option for a single-color display name. Keep it concise.',
});
const GRADIENT_DESCRIPTOR = msg({
	message: 'Gradient',
	comment: 'Option for a two-color gradient display name. Keep it concise.',
});
const START_COLOR_DESCRIPTOR = msg({
	message: 'Start color',
	comment: 'Label for the first color of the display name. Keep it concise.',
});
const END_COLOR_DESCRIPTOR = msg({
	message: 'End color',
	comment: 'Label for the second color of a gradient display name. Keep it concise.',
});
const PICK_A_FONT_FIRST_DESCRIPTOR = msg({
	message: 'Pick a font first, then choose the colors.',
	comment: 'Hint under the name color controls before a font is chosen. Keep the tone plain.',
});
const NAME_COLORS_READABLE_DESCRIPTOR = msg({
	message: 'Easy to read on dark backgrounds.',
	comment: 'Contrast check result under the display name colors when they pass.',
});
const NAME_COLORS_UNREADABLE_DESCRIPTOR = msg({
	message: 'These colors are hard to read on dark backgrounds. Pick lighter colors to save.',
	comment: 'Contrast check result under the display name colors when they fail; saving is blocked. Keep it specific.',
});
const PROFILE_THEME_DESCRIPTOR = msg({
	message: 'Profile theme',
	comment: 'Label for the two-color profile card theme controls. Keep it concise.',
});
const PROFILE_THEME_PRESET_DESCRIPTOR = msg({
	message: 'Use {top} to {bottom}',
	comment: 'Accessible name of a preset profile theme swatch. {top} and {bottom} are hex colors inserted by code.',
});
const TOP_COLOR_DESCRIPTOR = msg({
	message: 'Top color',
	comment: 'Label for the top color of the profile card theme. Keep it concise.',
});
const BOTTOM_COLOR_DESCRIPTOR = msg({
	message: 'Bottom color',
	comment: 'Label for the bottom color of the profile card theme. Keep it concise.',
});
const RESET_PLUS_APPEARANCE_DESCRIPTOR = msg({
	message: 'Reset name style and theme',
	comment: 'Button that clears the name style and the profile theme; the change is saved with the save bar.',
});

interface PlusAppearanceSettingsProps {
	displayName: string;
	value: PlusAppearanceValues;
	access: PlusAppearanceAccess;
	disabled?: boolean;
	errorMessage?: string;
	onNameStyleChange: (nameStyle: NameStyle | null) => void;
	onThemeColorsChange: (themeColors: ThemeColors | null) => void;
}

type NamePreviewVars = React.CSSProperties & {'--plus-name-1'?: string; '--plus-name-2'?: string};

function namePreviewStyle(fontKey: string, colors: ReadonlyArray<number>): NamePreviewVars {
	return {
		fontFamily: nameStyleFontFamily(fontKey),
		'--plus-name-1': colorToHex(colors[0]),
		'--plus-name-2': colors[1] === undefined ? undefined : colorToHex(colors[1]),
	};
}

interface FontCardProps {
	font: NameStyleFont;
	displayName: string;
	label: string;
	colors: ReadonlyArray<number>;
	gradient: boolean;
	defaultOnLight: boolean;
	disabled: boolean;
}

function FontCard({font, displayName, label, colors, gradient, defaultOnLight, disabled}: FontCardProps) {
	const cardRef = useRef<HTMLButtonElement | null>(null);
	return (
		<FocusRing
			focusTarget={cardRef}
			ringTarget={cardRef}
			offset={-2}
			enabled={!disabled}
			data-flx="user.my-profile-tab.plus-appearance-settings.font-card.focus-ring"
		>
			<RadixRadioGroup.Item
				ref={cardRef}
				value={String(font.id)}
				className={styles.fontCard}
				data-flx="user.my-profile-tab.plus-appearance-settings.font-card.item"
			>
				<span
					className={clsx(
						styles.fontSample,
						gradient && styles.fontSampleGradient,
						defaultOnLight && styles.fontSampleDefaultOnLight,
					)}
					style={namePreviewStyle(font.key, colors)}
					aria-hidden={true}
					data-flx="user.my-profile-tab.plus-appearance-settings.font-sample"
				>
					{displayName}
				</span>
				<span className={styles.fontLabel} data-flx="user.my-profile-tab.plus-appearance-settings.font-label">
					{label}
				</span>
				<RadixRadioGroup.Indicator
					className={styles.fontCheck}
					data-flx="user.my-profile-tab.plus-appearance-settings.font-check"
				>
					<CheckIcon weight="bold" data-flx="user.my-profile-tab.plus-appearance-settings.font-check-icon" />
				</RadixRadioGroup.Indicator>
			</RadixRadioGroup.Item>
		</FocusRing>
	);
}

export const PlusAppearanceSettings = observer(
	({
		displayName,
		value,
		access,
		disabled = false,
		errorMessage,
		onNameStyleChange,
		onThemeColorsChange,
	}: PlusAppearanceSettingsProps) => {
		const {i18n} = useLingui();
		const premiumProductName = getPremiumProductName();
		const nameStyle = value.name_style;
		const nameColors = nameStyle?.colors ?? [DEFAULT_NAME_COLOR];
		const nameMode: NameColorMode = nameColors.length === 2 ? 'gradient' : 'solid';
		const lastSecondColor = useRef(DEFAULT_SECOND_NAME_COLOR);
		if (nameColors[1] !== undefined) lastSecondColor.current = nameColors[1];
		const themeColors = value.theme_colors ?? PROFILE_THEME_PRESETS[0];
		const nameLocked = disabled || !access.nameStyle;
		const themeLocked = disabled || !access.profileTheme;
		const readable = isNameStyleReadable(nameStyle);
		const defaultOnLight = !isNameColorsReadableOnLight(nameColors);
		const previewSurface = profileGradientSurface(value.theme_colors);
		useEffect(() => {
			for (const font of NAME_STYLE_FONTS) requestNameStyleFont(font.key);
		}, []);
		return (
			<div className={styles.section} data-flx="user.my-profile-tab.plus-appearance-settings.section">
				<div data-flx="user.my-profile-tab.plus-appearance-settings.header">
					<h2 className={styles.title} data-flx="user.my-profile-tab.plus-appearance-settings.title">
						{i18n._(PLUS_APPEARANCE_DESCRIPTOR, {premiumProductName})}
					</h2>
					<p className={styles.description} data-flx="user.my-profile-tab.plus-appearance-settings.description">
						{i18n._(PLUS_APPEARANCE_DESCRIPTION_DESCRIPTOR)}
					</p>
				</div>
				<div className={styles.preview} data-flx="user.my-profile-tab.plus-appearance-settings.preview">
					<div className={styles.label} data-flx="user.my-profile-tab.plus-appearance-settings.preview-label">
						{i18n._(PROFILE_PREVIEW_DESCRIPTOR)}
					</div>
					<div
						className={clsx(styles.previewCard, previewSurface?.className)}
						style={previewSurface?.style}
						data-flx="user.my-profile-tab.plus-appearance-settings.preview-card"
					>
						<NameStyleText
							nameStyle={nameStyle}
							fontOnly={!nameReadsOnProfileTheme(nameStyle, value.theme_colors)}
							className={styles.previewName}
							data-flx="user.my-profile-tab.plus-appearance-settings.preview-name"
						>
							{displayName}
						</NameStyleText>
					</div>
				</div>
				{(!access.nameStyle || !access.profileTheme) && (
					<PlutoniumUpsell data-flx="user.my-profile-tab.plus-appearance-settings.plutonium-upsell">
						{i18n._(PLUS_APPEARANCE_UPSELL_DESCRIPTOR, {premiumProductName})}
					</PlutoniumUpsell>
				)}
				<div data-flx="user.my-profile-tab.plus-appearance-settings.fonts">
					<div className={styles.label} data-flx="user.my-profile-tab.plus-appearance-settings.fonts-label">
						{i18n._(NAME_FONT_DESCRIPTOR)}
					</div>
					<RadixRadioGroup.Root
						className={styles.fontGrid}
						value={nameStyle ? String(nameStyle.font_id) : undefined}
						onValueChange={(fontId) => onNameStyleChange(withNameFont(nameStyle, Number(fontId) as NameStyleFontId))}
						disabled={nameLocked}
						loop={true}
						aria-label={i18n._(NAME_FONT_DESCRIPTOR)}
						data-font-grid=""
						data-flx="user.my-profile-tab.plus-appearance-settings.font-grid"
					>
						{NAME_STYLE_FONTS.map((font) => (
							<FontCard
								key={font.id}
								font={font}
								displayName={displayName}
								label={i18n._(FONT_STYLE_DESCRIPTORS[font.key])}
								colors={nameColors}
								gradient={nameMode === 'gradient'}
								defaultOnLight={defaultOnLight}
								disabled={nameLocked}
								data-flx="user.my-profile-tab.plus-appearance-settings.font-card"
							/>
						))}
					</RadixRadioGroup.Root>
				</div>
				<div className={styles.group} data-flx="user.my-profile-tab.plus-appearance-settings.name-color">
					<div className={styles.label} data-flx="user.my-profile-tab.plus-appearance-settings.name-color-label">
						{i18n._(NAME_COLOR_DESCRIPTOR)}
					</div>
					<RadioGroup
						className={styles.modeGroup}
						options={[
							{value: 'solid' as NameColorMode, name: i18n._(SOLID_DESCRIPTOR)},
							{value: 'gradient' as NameColorMode, name: i18n._(GRADIENT_DESCRIPTOR)},
						]}
						value={nameMode}
						disabled={nameLocked || !nameStyle}
						onChange={(mode) =>
							nameStyle && onNameStyleChange(withNameColorMode(nameStyle, mode, lastSecondColor.current))
						}
						aria-label={i18n._(NAME_COLOR_DESCRIPTOR)}
						data-flx="user.my-profile-tab.plus-appearance-settings.radio-group.mode"
					/>
					<div className={styles.colorRow} data-flx="user.my-profile-tab.plus-appearance-settings.name-colors">
						<ColorPickerField
							label={i18n._(nameMode === 'gradient' ? START_COLOR_DESCRIPTOR : NAME_COLOR_DESCRIPTOR)}
							value={nameColors[0]}
							onChange={(color) => nameStyle && onNameStyleChange(withNameColor(nameStyle, 0, color))}
							disabled={nameLocked || !nameStyle}
							hideHelperText={true}
							data-flx="user.my-profile-tab.plus-appearance-settings.color-picker-field.name-1"
						/>
						{nameMode === 'gradient' && (
							<ColorPickerField
								label={i18n._(END_COLOR_DESCRIPTOR)}
								value={nameColors[1]}
								onChange={(color) => nameStyle && onNameStyleChange(withNameColor(nameStyle, 1, color))}
								disabled={nameLocked}
								hideHelperText={true}
								data-flx="user.my-profile-tab.plus-appearance-settings.color-picker-field.name-2"
							/>
						)}
					</div>
					<p
						className={clsx(styles.contrast, !readable && styles.contrastWarning)}
						data-readable={readable}
						role={readable ? undefined : 'alert'}
						data-flx="user.my-profile-tab.plus-appearance-settings.contrast"
					>
						{nameStyle
							? i18n._(readable ? NAME_COLORS_READABLE_DESCRIPTOR : NAME_COLORS_UNREADABLE_DESCRIPTOR)
							: i18n._(PICK_A_FONT_FIRST_DESCRIPTOR)}
					</p>
				</div>
				<div className={styles.group} data-flx="user.my-profile-tab.plus-appearance-settings.theme">
					<div className={styles.label} data-flx="user.my-profile-tab.plus-appearance-settings.theme-label">
						{i18n._(PROFILE_THEME_DESCRIPTOR)}
					</div>
					<div className={styles.presets} data-flx="user.my-profile-tab.plus-appearance-settings.presets">
						{PROFILE_THEME_PRESETS.map(([top, bottom]) => {
							const selected = value.theme_colors?.[0] === top && value.theme_colors?.[1] === bottom;
							return (
								<button
									key={`${top}-${bottom}`}
									type="button"
									className={clsx(styles.preset, selected && styles.presetSelected)}
									style={{backgroundImage: `linear-gradient(180deg, ${colorToHex(top)}, ${colorToHex(bottom)})`}}
									aria-pressed={selected}
									aria-label={i18n._(PROFILE_THEME_PRESET_DESCRIPTOR, {
										top: colorToHex(top).toUpperCase(),
										bottom: colorToHex(bottom).toUpperCase(),
									})}
									disabled={themeLocked}
									onClick={() => onThemeColorsChange([top, bottom])}
									data-flx="user.my-profile-tab.plus-appearance-settings.preset.button"
								/>
							);
						})}
					</div>
					<div className={styles.colorRow} data-flx="user.my-profile-tab.plus-appearance-settings.theme-colors">
						<ColorPickerField
							label={i18n._(TOP_COLOR_DESCRIPTOR)}
							value={themeColors[0]}
							onChange={(color) => onThemeColorsChange(withThemeColor(value.theme_colors, 0, color))}
							disabled={themeLocked}
							hideHelperText={true}
							data-flx="user.my-profile-tab.plus-appearance-settings.color-picker-field.theme-1"
						/>
						<ColorPickerField
							label={i18n._(BOTTOM_COLOR_DESCRIPTOR)}
							value={themeColors[1]}
							onChange={(color) => onThemeColorsChange(withThemeColor(value.theme_colors, 1, color))}
							disabled={themeLocked}
							hideHelperText={true}
							data-flx="user.my-profile-tab.plus-appearance-settings.color-picker-field.theme-2"
						/>
					</div>
				</div>
				<div data-flx="user.my-profile-tab.plus-appearance-settings.actions">
					<Button
						variant="secondary"
						small={true}
						onClick={() => {
							onNameStyleChange(null);
							onThemeColorsChange(null);
						}}
						disabled={disabled || (nameStyle === null && value.theme_colors === null)}
						data-flx="user.my-profile-tab.plus-appearance-settings.button.reset"
					>
						{i18n._(RESET_PLUS_APPEARANCE_DESCRIPTOR)}
					</Button>
				</div>
				{errorMessage && (
					<p className={styles.errorMessage} data-flx="user.my-profile-tab.plus-appearance-settings.error-message">
						{errorMessage}
					</p>
				)}
			</div>
		);
	},
);
